/**
 * Shared Reverb pricing helpers used by PriceIndicator and thumbnail badges.
 * Keep PXC_TO_REVERB in sync with functions/fetchReverbPricing.js.
 */

export const PXC_TO_REVERB = {
  New: 'new',
  Excellent: 'excellent',
  'Very Good': 'very_good',
  Good: 'good',
  Poor: 'fair',
  Damaged: 'poor',
};

// MUST mirror functions/fetchReverbPricing.js#VINTAGE_PRICING_DECADES so the
// client requests (and the server stores) the same pedalPricing/{key} doc id.
// Only pre-2000 decades get their own bucket; 2000s/2010s/2020s collapse to
// 'modern' because Reverb's year filter drops the (year-less) majority of
// modern-production listings, leaving recent-decade queries near-empty.
const VINTAGE_PRICING_DECADES = new Set(['1970s', '1980s', '1990s']);

export function normalizeDecade(d) {
  const raw = String(d || '').toLowerCase().trim();
  if (VINTAGE_PRICING_DECADES.has(raw)) return raw;
  return 'modern';
}

/**
 * Normalize ONE identity part (a make or a model) for the document key.
 *
 * MEANINGFUL-CHARACTER IDENTITY (owner instruction 2026-10-12, after an
 * independent reviewer executed this builder and found the collision):
 *   buildPedalPricingKey('DigiTech', 'Trio',  'modern') -> digitech_trio_modern
 *   buildPedalPricingKey('DigiTech', 'Trio+', 'modern') -> digitech_trio_modern
 * Two DIFFERENT products shared one storage identity, so they could read
 * each other's cached document, overwrite each other's refresh, and pool
 * each other's transaction evidence. Fixing the incoming Reverb matcher was
 * not enough: identity has to hold all the way through STORAGE.
 *
 * '+' is therefore preserved in the key. It is the character this catalog
 * actually uses to distinguish products (DigiTech Trio+, MXR Distortion+,
 * MXR Script Distortion+, TC Electronic Ditto+), and it is legal in a
 * Firestore document id.
 *
 * COMPATIBILITY IS EXACT: the character class is the only change, so every
 * identity that contains no '+' produces a byte-identical key to before and
 * every existing document keeps its name. Only the '+' models move, and
 * they move off a key they were wrongly sharing.
 *
 * NOT changed, and MEASURED rather than assumed: non-ASCII letters still
 * fold to a separator, so Walrus Audio's 'Slö' keys as 'sl'. A fold like
 * that can SPLIT one product across two keys AND, in principle, MERGE two
 * distinct names onto one — the catalog guard in
 * test/pricingKeyParity.test.js proves the merge case is reachable with a
 * synthetic 'Slö' / 'SL' pair. Measured scope in today's catalog: 2
 * affected entries, 0 merges. Transliteration would address both but
 * RENAMES an existing document, so it is an owner decision, not a silent
 * change. Open item.
 *
 * MIRROR: functions/fetchReverbPricing.js has the identical function and
 * functions/pedalPricingSamples.js#normalizeString uses the same character
 * class. All are pinned against each other by
 * functions/test/pricingKeyParity.test.js.
 */
export function normalizeIdentityPart(s) {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9+]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function buildPedalPricingKey(make, model, decade) {
  const m = normalizeIdentityPart(make);
  const mo = normalizeIdentityPart(model);
  if (!m || !mo) return '';
  return `${m}_${mo}_${normalizeDecade(decade)}`;
}

export function formatDollarsFromCents(cents) {
  if (!Number.isFinite(cents)) return '$0';
  return `$${(cents / 100).toFixed(0)}`;
}

export function formatMarketLabel(d) {
  return d === 'modern' ? 'Current market' : `${d} market`;
}

export function getTierFromPricingDoc(pricingDoc, condition) {
  if (!pricingDoc || !pricingDoc.conditions) return null;
  const reverbKey = PXC_TO_REVERB[condition];
  if (!reverbKey) return null;
  const tier = pricingDoc.conditions[reverbKey];
  if (!tier || !Number.isFinite(tier.low) || !Number.isFinite(tier.high)) return null;
  if (tier.high <= tier.low) return null;
  return tier;
}

/**
 * Display-only tier for the price-check bar. Fair (the median) must never read
 * as the SAME dollar amount as either endpoint — not the High (tight listings
 * where median === 75th pct) and not the Low (median === 25th pct). When it
 * collides with either, recalculate Fair as the midpoint of Low and High
 * (nearest dollar) and clamp it strictly inside the range so it always shows a
 * distinct price. Backend percentiles in Firestore are unchanged.
 */
export function getDisplayTier(tier) {
  if (!tier) return null;
  const lowLabel = formatDollarsFromCents(tier.low);
  const midLabel = formatDollarsFromCents(tier.mid);
  const highLabel = formatDollarsFromCents(tier.high);
  if (midLabel !== lowLabel && midLabel !== highLabel) return tier;

  let displayMid = Math.round((tier.low + tier.high) / 200) * 100;
  // Keep Fair strictly between Low and High when there's at least $2 of room
  // (so a distinct whole-dollar value exists). Below that the endpoints
  // themselves collapse to the same dollar and no distinct midpoint is possible.
  const lowDollars = Math.round(tier.low / 100);
  const highDollars = Math.round(tier.high / 100);
  if (highDollars - lowDollars >= 2) {
    const clampLo = (lowDollars + 1) * 100;
    const clampHi = (highDollars - 1) * 100;
    displayMid = Math.min(clampHi, Math.max(clampLo, displayMid));
  }
  return { ...tier, mid: displayMid };
}

/**
 * Piecewise position on the price-check bar (0–100), matching PriceIndicator.
 */
export function getPriceBarPositionPct(tier, priceDollars) {
  const priceCents = Math.round((Number(priceDollars) || 0) * 100);
  if (priceCents <= 0) return null;

  const midUsable =
    Number.isFinite(tier.mid) && tier.mid > tier.low && tier.mid < tier.high;

  if (priceCents <= tier.low) return 0;
  if (priceCents >= tier.high) return 100;
  if (!midUsable) {
    return ((priceCents - tier.low) / (tier.high - tier.low)) * 100;
  }
  if (priceCents <= tier.mid) {
    return ((priceCents - tier.low) / (tier.mid - tier.low)) * 50;
  }
  return 50 + ((priceCents - tier.mid) / (tier.high - tier.mid)) * 50;
}

/** 0 = good value, 1 = fair, 2 = high. Null when price missing or unusable. */
export function getPriceZoneIndex(tier, priceDollars) {
  const pctPosition = getPriceBarPositionPct(tier, priceDollars);
  if (pctPosition == null) return null;
  if (pctPosition < 100 / 3) return 0;
  if (pctPosition < 200 / 3) return 1;
  return 2;
}

/**
 * Thumbnail badge only — stricter than getPriceZoneIndex.
 *   good: bottom third of the "Good value" bar segment (~0–11% on the bar)
 *   high: full "High" segment (same as price-check bar, right third)
 *   null: fair zone, or good value but not low enough for "Good deal"
 */
export function getThumbnailDealBadge(tier, priceDollars) {
  // Owner boundary rule (2026-10-09): "Above market" ONLY when the price is
  // strictly above the range's high, "Good deal" ONLY strictly below its low,
  // and NO badge anywhere inside the range — endpoints included. Replaced the
  // old bar-position rule (top third = high, deep bottom ninth = good), which
  // badged prices still inside the market range. For a collapsed band
  // (low === high) this degenerates to strict above/below the reference.
  if (!tier || !Number.isFinite(tier.low) || !Number.isFinite(tier.high)) return null;
  const cents = Math.round((Number(priceDollars) || 0) * 100);
  if (cents <= 0) return null;
  if (cents > tier.high) return 'high';
  if (cents < tier.low) return 'good';
  return null;
}

export function buildReverbPricingTooltip({ condition, normDecade, tier }) {
  const displayTier = getDisplayTier(tier);
  const lowLabel = formatDollarsFromCents(displayTier.low);
  const midLabel = formatDollarsFromCents(displayTier.mid);
  const highLabel = formatDollarsFromCents(displayTier.high);
  return (
    `Reverb listings · ${condition} · ${formatMarketLabel(normDecade)} · ` +
    `${lowLabel}–${highLabel} (median ${midLabel})`
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * v2 market-tier evaluator — the single gate every pricing consumer uses.
 *
 * Pipeline v2 docs (pricingMeta.pipelineVersion === 2, written by
 * functions/fetchReverbPricing.js per REVERB_PRICING_AUDIT_2026-10-08.md,
 * final display direction Part 13) carry per-tier aggregates in
 * `conditionsV2` and per-listing evidence in `evidenceV2`. Kinds:
 *
 *   'verdict' — the production Good/Fair/High presentation, unchanged. Only
 *     tiers that passed the server's full verdict checklist (Reverb evidence
 *     alone; full coverage or the representative partial basis) and are
 *     still fresh (doc 14 days or newer).
 *   'single'  — the middle band collapsed to one reference price. Rendered
 *     as "Market price $X" with no invented spread; the high-price warning
 *     still fires above the reference.
 *   'range'   — "Market price $low – $high"; info.limited marks tiny samples
 *     (under 5 listings) for the small "Limited data" note. All other
 *     disclosures (source counts, sampled basis, condition mix) belong in
 *     the existing tooltip, never new on-screen chrome.
 *   'legacy'  — a v1-era doc, or v2 data older than 28 days: same rendering
 *     as 'range' but attributed to "prior data", never "Current market".
 *   'none'    — nothing renders.
 *
 * Freshness and the value ladder mirror functions/reverbPricingV2.js
 * (28d display / 14d verdict / quartiles from 5+, observed span below) —
 * hand-maintained mirror, change both together. Self-exclusion: the listing
 * being evaluated must not sit in its own benchmark, so pass its PXC listing
 * id and any matching `pxc:<id>` evidence entry is removed, with the value
 * recomputed from what remains.
 * ──────────────────────────────────────────────────────────────────────────── */

const V2_DISPLAY_MAX_AGE_DAYS = 28;
const V2_VERDICT_MAX_AGE_DAYS = 14;
const V2_MIN_FOR_QUARTILES = 5;
const DAY_MS = 86400000;

// Type-7 percentile — mirror of functions/reverbPricingV2.js#percentile.
function percentileV2(sortedAsc, p) {
  if (!sortedAsc.length) return null;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = (sortedAsc.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  return Math.round(sortedAsc[lo] + (idx - lo) * (sortedAsc[hi] - sortedAsc[lo]));
}

// Value ladder mirror: 5+ → quartile band; 1–4 → observed min/median/max.
function ladderRange(sortedAsc) {
  if (sortedAsc.length >= V2_MIN_FOR_QUARTILES) {
    return {
      low: percentileV2(sortedAsc, 0.25),
      mid: percentileV2(sortedAsc, 0.5),
      high: percentileV2(sortedAsc, 0.75),
    };
  }
  return {
    low: sortedAsc[0],
    mid: percentileV2(sortedAsc, 0.5),
    high: sortedAsc[sortedAsc.length - 1],
  };
}

// Owner ruling 2026-10-12: PXC ASKING PRICES NEVER COUNT. The range is
// completed PXC sales and trades plus Reverb's active asks (Reverb's own
// sold-data model). Enforced at read time too, so a document written before
// the ruling stops counting its stored PXC asks the moment the app updates.
function currentV2Entries(evidence, nowMs) {
  const floor = nowMs - V2_DISPLAY_MAX_AGE_DAYS * DAY_MS;
  return (Array.isArray(evidence) ? evidence : []).filter(
    (e) => e && !e.excluded && !e.retired && e.src !== 'pxc' &&
      Number.isFinite(e.lastSeenAt) && e.lastSeenAt >= floor &&
      Number.isFinite(e.cents) && e.cents > 0
  );
}

// ── Better-condition floor: mirror of functions/reverbPricingV2.js ──────────
// #TIER_RANK / #betterConditionFloor / #floorBand (hand-maintained, parity-
// pinned). OWNER RULE (2026-10-12, Strymon Deco V1): a worse-condition pedal
// cannot be worth more than the cheapest better-condition one for sale, so a
// tier's LOW end is capped at that price; the high end is untouched and the
// mid is re-taken as the median of the tier's own prices plus the floor. The
// server applies this when it writes the document; the client must re-apply
// it after the self-excluded recompute, which rebuilds the band from scratch.
const V2_TIER_ORDER = ['new', 'mint_open_box', 'excellent', 'very_good', 'good', 'fair', 'poor'];
const V2_TIER_RANK = Object.fromEntries(V2_TIER_ORDER.map((k, i) => [k, i]));

// The floor is the cheapest DISPLAYED low among the better tiers (what the
// customer is shown for them), never a raw listing: a quartile band hides
// its bottom quarter as noise, and one outlier must not set another tier's
// low. Better tiers are untouched by self-exclusion (the viewed listing is
// in its own tier), so their stored, server-floored lows are read directly.
function betterConditionFloorV2(conditionsV2, tierKey) {
  const myRank = V2_TIER_RANK[tierKey];
  if (myRank === undefined) return null;
  let best = null;
  for (const k of V2_TIER_ORDER) {
    if (V2_TIER_RANK[k] >= myRank) continue;
    const t = conditionsV2 ? conditionsV2[k] : null;
    if (!t || !t.display || !Number.isFinite(t.low)) continue;
    if (!best || t.low < best.cents) best = { cents: t.low, tierKey: k };
  }
  return best;
}

function floorBandV2(band, ownCents, floor) {
  if (!band || !floor || !Number.isFinite(band.low) || floor.cents >= band.low) return { band, floor: null };
  const sorted = [...ownCents, floor.cents].sort((a, b) => a - b);
  return { band: { ...band, low: floor.cents, mid: percentileV2(sorted, 0.5) }, floor };
}

/**
 * CONTEXT ONLY, never a valuation (reviewer boundary, 2026-10-12): when the
 * chosen condition has no pricing, the nearest condition WITH asking data is
 * offered as an explicitly labelled comparison ("No Very Good pricing
 * available. Excellent-condition asking range: $163 to $230."). It is
 * rendered by PriceIndicator alone; evaluateMarketTier never returns it, so
 * no deal verdict, warning or trade balance can ever rest on it. Nearest =
 * smallest rank distance, better conditions first on a tie; asking data
 * only (transaction-informed bands are not borrowed across conditions).
 */
export function nearestConditionAskContext(pricingDoc, condition, { nowMs } = {}) {
  if (!pricingDoc || !pricingDoc.pricingMeta || pricingDoc.pricingMeta.pipelineVersion !== 2) return null;
  const own = evaluateAskEvidence(pricingDoc, condition, { nowMs });
  if (own.kind !== 'none') return null;
  const myKey = PXC_TO_REVERB[condition];
  const myRank = V2_TIER_RANK[myKey];
  if (myRank === undefined) return null;
  const candidates = Object.keys(PXC_TO_REVERB)
    .filter((c) => c !== condition && V2_TIER_RANK[PXC_TO_REVERB[c]] !== undefined)
    .map((c) => ({ condition: c, rank: V2_TIER_RANK[PXC_TO_REVERB[c]] }))
    .sort((a, b) => Math.abs(a.rank - myRank) - Math.abs(b.rank - myRank) || a.rank - b.rank);
  for (const c of candidates) {
    const e = evaluateAskEvidence(pricingDoc, c.condition, { nowMs });
    if (e.kind !== 'verdict' && e.kind !== 'single' && e.kind !== 'range') continue;
    const low = e.kind === 'single' ? e.priceCents : e.tier.low;
    const high = e.kind === 'single' ? e.priceCents : e.tier.high;
    if (!Number.isFinite(low) || !Number.isFinite(high)) continue;
    return { condition: c.condition, low, high, single: low === high, better: c.rank < myRank };
  }
  return null;
}

function legacyKind(pricingDoc, condition) {
  const tier = getTierFromPricingDoc(pricingDoc, condition);
  if (!tier) return { kind: 'none' };
  return { kind: 'legacy', tier, info: { priorData: true } };
}

export function evaluateMarketTier(pricingDoc, condition, { excludeListingId, nowMs } = {}) {
  const askEvaluation = evaluateAskEvidence(pricingDoc, condition, { excludeListingId, nowMs });
  return applyMarketDisplay(pricingDoc, condition, askEvaluation, { nowMs, excludeListingId });
}

// The pre-I-4 evaluator: asking-price evidence only (Reverb + PXC listings).
// Its result is either returned unchanged (no transaction evidence) or
// becomes the ASK ANCHOR of the staged blend below.
function evaluateAskEvidence(pricingDoc, condition, { excludeListingId, nowMs } = {}) {
  if (!pricingDoc) return { kind: 'none' };
  const meta = pricingDoc.pricingMeta;
  if (!meta || meta.pipelineVersion !== 2) return legacyKind(pricingDoc, condition);

  const tierKey = PXC_TO_REVERB[condition];
  const t = tierKey && pricingDoc.conditionsV2 ? pricingDoc.conditionsV2[tierKey] : null;
  if (!t || !t.display) return { kind: 'none' };

  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const ageDays = Number.isFinite(meta.generatedAtMs)
    ? (now - meta.generatedAtMs) / DAY_MS
    : Infinity;

  // Self-exclusion over the evidence. Evidence timestamps are relative to the
  // doc's own generation time; the doc-level ageDays gate handles staleness.
  const evidence = pricingDoc.evidenceV2 ? pricingDoc.evidenceV2[tierKey] : null;
  let entries = currentV2Entries(evidence, Number.isFinite(meta.generatedAtMs) ? meta.generatedAtMs : now);
  // A document written BEFORE the PXC-asks ruling has PXC asks baked into
  // its stored band and counts; rebuild from the Reverb entries instead
  // (currentV2Entries already dropped the PXC ones). The old self-exclusion
  // can no longer find anything to remove — kept as a harmless no-op.
  const storedIncludesPxc = (t.pxcListings || 0) > 0;
  const selfId = excludeListingId ? `pxc:${excludeListingId}` : null;
  const hadSelf = storedIncludesPxc || (selfId ? entries.some((e) => e.id === selfId) : false);
  if (selfId) entries = entries.filter((e) => e.id !== selfId);
  const ro = t.reverbOnly;
  // A pre-ruling tier whose server already computed a Reverb-only band:
  // use it directly (no evidence rebuild needed).
  const useReverbOnlyBand = storedIncludesPxc && !!ro && Number.isFinite(ro.low) && Number.isFinite(ro.high);
  if (hadSelf && entries.length === 0 && !useReverbOnlyBand) return { kind: 'none' };

  let low = t.low, mid = t.mid, high = t.high;
  let uniqueListings = t.uniqueListings;
  let pxcCount = 0;
  let lowFromBetterCondition = t.lowFromBetterCondition || null;
  const sorted = entries.map((e) => e.cents).sort((a, b) => a - b);
  if (hadSelf) {
    if (useReverbOnlyBand) {
      ({ low, mid, high } = { low: ro.low, mid: Number.isFinite(ro.mid) ? ro.mid : Math.round((ro.low + ro.high) / 2), high: ro.high });
      uniqueListings = t.reverbListings || entries.length;
    } else {
      ({ low, mid, high } = ladderRange(sorted));
      uniqueListings = entries.length;
    }
  }
  // The better-condition floor is applied AT READ TIME for every document:
  // a self-excluded recompute rebuilds the band from raw prices, and a
  // document written before the rule existed carries an unfloored low. On
  // a document the server already floored this is a no-op, so the two
  // sides can never disagree (parity-pinned).
  if (Number.isFinite(low)) {
    const floored = floorBandV2({ low, mid, high }, sorted,
      betterConditionFloorV2(pricingDoc.conditionsV2, tierKey));
    if (floored.floor) {
      ({ low, mid, high } = floored.band);
      lowFromBetterCondition = floored.floor;
    }
  }
  if (!Number.isFinite(low) || !Number.isFinite(high)) return { kind: 'none' };

  const tier = { low, mid, high, sampleSize: uniqueListings };
  const countAtReference = entries.filter((e) => e.cents === low).length;
  const info = {
    reverbCount: hadSelf ? uniqueListings : (t.reverbListings || 0),
    pxcCount,
    totalListings: uniqueListings,
    apiTotal: Number.isFinite(t.apiTotal) ? t.apiTotal : null,
    coverage: t.coverage || 'full',
    conditionCounts: t.conditionCounts || null,
    mixedConditions: t.mixedConditions === true,
    mixedVersions: t.mixedVersions === true,
    limited: uniqueListings < V2_MIN_FOR_QUARTILES,
    // The server's verdict-blocking reasons, passed through so the tooltip
    // can name WHY a displayed price does not carry the comparison.
    reasons: Array.isArray(t.reasons) ? t.reasons : [],
    ageDays: Math.floor(ageDays),
    priorData: false,
    // Where the low end came from when a better-condition listing set it
    // (owner rule); null when the tier's own prices set it.
    lowFromBetterCondition: lowFromBetterCondition || null,
  };

  // v2 data older than 28 days is prior data, never "Current market".
  if (ageDays > V2_DISPLAY_MAX_AGE_DAYS) {
    return { kind: 'legacy', tier, info: { ...info, priorData: true } };
  }

  // Verdict eligibility is the SERVER's Reverb-only decision; excluding a PXC
  // self-entry cannot change it (PXC evidence never feeds verdicts).
  const verdictAllowed =
    t.verdictEligible === true &&
    meta.verdictsEnabled !== false &&
    ageDays <= V2_VERDICT_MAX_AGE_DAYS;

  // Review finding 4: verdict PRICES are the Reverb-only aggregate — a PXC
  // listing can never enable a verdict, and must never move one's numbers
  // either. Combined (Reverb + PXC) values remain the qualified-range basis.
  const r = t.reverbOnly;
  const reverbTier = r && Number.isFinite(r.low) && Number.isFinite(r.high)
    ? { low: r.low, mid: r.mid, high: r.high, sampleSize: t.reverbListings || uniqueListings }
    : tier;

  if (verdictAllowed && reverbTier.low === reverbTier.high) {
    // Strong single reference: Reverb-only evidence, verdict-grade. Needs no
    // Limited-data note merely because all prices coincide.
    return {
      kind: 'single', tier: reverbTier, priceCents: reverbTier.low,
      countAtReference: entries.filter((e) => e.src !== 'pxc' && e.cents === reverbTier.low).length,
      qualified: true, info,
    };
  }
  if (verdictAllowed) return { kind: 'verdict', tier: reverbTier, info };

  if (low === high) {
    // Weak single reference (combined band collapsed without verdict-grade
    // evidence): shown with the Limited-data note.
    return { kind: 'single', tier, priceCents: low, countAtReference, qualified: false, info };
  }
  return { kind: 'range', tier, info };
}

/* ────────────────────────────────────────────────────────────────────────────
 * R-1: the transaction-informed stage, PRIVATE-EVIDENCE architecture
 * (owner directive + implementation authorization, 2026-10-09).
 *
 * The client no longer sees transaction rows or any intermediate
 * calculation input. The server (functions/marketDisplayV1.js, computed
 * inside the recompute and refresh transactions) publishes ONLY
 * pedalPricing/{key}.marketDisplayV1 — the final display result per
 * condition with minimal, explicitly selected fields:
 *   { kind, low, mid, high, priceCents?, limited, qualified,
 *     estimateBased, showReverbAttribution, staleAfterMs }
 * gated by PUBLICATION_GATE_V1 (3 in-window rows / 3 distinct
 * participants — provisional) and the server-side caveat policy. The §28
 * blend, the publication gate, and the caveat sufficiency all run
 * server-side; src/utils/pricingBlendV1.js remains ONLY as the parity
 * cross-check on the server twin, never in this display path.
 *
 * EXPIRY (owner requirement): entries carry staleAfterMs — the first
 * moment a contributing observation exits the evidence window. This
 * evaluator enforces it AT READ TIME, so an expired entry is ignored
 * (ask-only fallback) immediately, cached documents included; the next
 * server write (event-driven recompute, or the sweep refresh re-blend)
 * rewrites the block.
 *
 * Display policy unchanged: the entry renders through the SAME
 * "Market price $X–$Y" / "Market price $X" treatment, one generic caveat
 * driven by `limited`, never a verdict, no provenance in customer text,
 * the strict above-high warning against the published band.
 * ──────────────────────────────────────────────────────────────────────────── */

// Mirror of functions/marketDisplayV1.js#DISPLAY_VERSION (hand-maintained
// like the matching engine; the end-to-end test imports the server constant
// and pins equality, so drift fails CI). An UNSUPPORTED version is ignored
// deliberately — the evaluator falls back to ask-only evidence rather than
// risk misreading a future schema. Exported for tests.
export const SUPPORTED_DISPLAY_VERSION = 'DISPLAY_V1';

function applyMarketDisplay(pricingDoc, condition, askEvaluation, { nowMs, excludeListingId } = {}) {
  const block = pricingDoc && pricingDoc.marketDisplayV1;
  if (!block || block.version !== SUPPORTED_DISPLAY_VERSION) return askEvaluation;
  const defaultEntry = block.byCondition ? block.byCondition[condition] : null;
  if (!defaultEntry) return askEvaluation;

  // OWN-LISTING EXCLUSION, viewer-independent (owner-approved policy
  // 2026-10-12). Three states, and the wrong one is a policy violation:
  //   (a) this listing contributes NO asking evidence → the default band
  //       already excludes it;
  //   (b) it contributes AND its variant was published → use the variant;
  //   (c) it contributes but NO variant exists (cap, size budget,
  //       generation failure, or any unexplained absence) → the default
  //       band would contain this listing's own asking price, so fall
  //       back to the ask-only SELF-EXCLUDED evaluation already computed
  //       by the caller (which is 'none' when nothing usable remains).
  // Presence is read from the PUBLIC asking evidence, so the decision
  // depends only on which listing is on screen — never on the viewer.
  let entry = defaultEntry;
  if (excludeListingId) {
    const tierKey = PXC_TO_REVERB[condition];
    const meta = pricingDoc.pricingMeta;
    const refMs = meta && Number.isFinite(meta.generatedAtMs) ? meta.generatedAtMs : (nowMs || Date.now());
    const evidence = tierKey && pricingDoc.evidenceV2 ? pricingDoc.evidenceV2[tierKey] : null;
    const contributes = currentV2Entries(evidence, refMs)
      .some((e) => e.id === `pxc:${excludeListingId}`);
    if (contributes) {
      const variant = defaultEntry.excluded ? defaultEntry.excluded[excludeListingId] : null;
      if (!variant || !Number.isFinite(variant.low) || !Number.isFinite(variant.high)) {
        return askEvaluation; // state (c) — never the self-including default
      }
      entry = variant;
    }
  }
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  // EXPIRY at read time: past staleAfterMs the published numbers no longer
  // match what a recompute would produce, so the entry is treated as
  // absent until the server rewrites it.
  if (!Number.isFinite(entry.staleAfterMs) || now > entry.staleAfterMs) return askEvaluation;
  if (!Number.isFinite(entry.low) || !Number.isFinite(entry.high)) return askEvaluation;

  const tier = {
    low: entry.low,
    mid: Number.isFinite(entry.mid) ? entry.mid : Math.round((entry.low + entry.high) / 2),
    high: entry.high,
  };
  const info = {
    ...(askEvaluation.info || {}),
    limited: entry.limited === true,
    // Never the "prior data" classification — expiry handles currency.
    priorData: false,
  };
  const base = {
    tier,
    info,
    transactionInformed: entry.estimateBased === true,
    // Display semantics from the server (never rendered as text): whether
    // the Reverb attribution line applies to this band.
    showReverbAttribution: entry.showReverbAttribution !== false,
    // Carried so a MOUNTED display can arm a re-evaluation timer at the
    // stamp (PriceIndicator). Expiry is otherwise enforced at the NEXT
    // evaluation only — a memoized result does not re-run on its own.
    staleAfterMs: entry.staleAfterMs,
  };
  if (entry.kind === 'single') {
    return {
      kind: 'single',
      ...base,
      priceCents: Number.isFinite(entry.priceCents) ? entry.priceCents : entry.low,
      countAtReference: 0,
      qualified: entry.qualified === true,
    };
  }
  return { kind: 'range', ...base };
}

// Friendly labels for the condition-mix disclosure in tooltips.
const SLUG_LABELS = {
  new: 'Brand New', mint: 'Mint', 'mint-inventory': 'Open Box',
  excellent: 'Excellent', 'very-good': 'Very Good', good: 'Good',
  fair: 'Fair', poor: 'Poor', pxc: 'PedalxChange',
};

/**
 * The small "Limited data" note appears under ANY displayed price or
 * reference that does not qualify for the supported comparison: thin
 * samples, Mint supplementation, partial coverage, stale or legacy
 * evidence, unresolved version matching, a weak single reference. A
 * verdict never shows it, and neither does a single reference whose
 * evidence would carry a verdict (all prices merely coinciding is not a
 * limitation). The SPECIFIC reason lives in the tooltip.
 */
export function shouldShowLimitedNote(evaluation) {
  if (!evaluation) return false;
  // Transaction-informed entries carry the server-decided caveat verbatim
  // (flags and the sufficiency policy are folded into `limited` at
  // computation time — the client renders, it does not re-decide).
  if (evaluation.transactionInformed) return evaluation.info.limited === true;
  if (evaluation.kind === 'range' || evaluation.kind === 'legacy') return true;
  if (evaluation.kind === 'single') return evaluation.qualified !== true;
  return false;
}

// Server verdict-blocking reasons → tooltip phrases. Reasons already covered
// by other tooltip tokens (mixed_conditions → the condition mix; mixed
// versions, partial, limited → their own tokens) are omitted here.
const REASON_PHRASES = {
  reverb_below_verdict_sellers: 'few sellers',
  reverb_seller_concentration: 'few sellers',
  reverb_observations_stale: 'not recently updated',
};

/**
 * Tooltip for any evaluated state — the ONE place limitations are disclosed
 * (Part 13: tooltips carry source counts, sampled basis, condition mix, and
 * the specific reason the comparison is absent; the visible UI adds at most
 * the short "Limited data" note).
 */
export function buildMarketTooltip({ condition, normDecade, evaluation }) {
  if (!evaluation || evaluation.kind === 'none') return '';
  // Transaction-informed bands are NOT "Reverb listings" — this internal
  // formatter names the real basis instead. Never rendered to customers.
  // (R-1: counts/contributions are server-private now; even internally the
  // client only knows the band is blended market evidence.)
  if (evaluation.transactionInformed) {
    return (
      `Blended market evidence · ${condition} · ` +
      `${formatDollarsFromCents(evaluation.tier.low)}–${formatDollarsFromCents(evaluation.tier.high)}`
    );
  }
  const { kind, tier, info } = evaluation;
  // Old-format/stale data keeps its INTERNAL classification (no verdicts, no
  // warnings) but shows no market-era label to customers: neither "prior
  // data" (owner: removed from customer-facing text) nor "Current market"
  // (banned for legacy data) — the segment is simply omitted.
  const isPrior = kind === 'legacy' || (info && info.priorData);
  const parts = [isPrior
    ? `Reverb listings · ${condition}`
    : `Reverb listings · ${condition} · ${formatMarketLabel(normDecade)}`];

  if (kind === 'legacy' && (!info || !Number.isFinite(info.totalListings))) {
    // v1 docs: no counts — their sample sizes overstate (weekly re-counting).
    return parts.join(' · ');
  }

  if (kind === 'single') {
    const count = evaluation.countAtReference || 0;
    const total = info.totalListings || count;
    parts.push(count === total
      ? `${total} US listings, all ${formatDollarsFromCents(evaluation.priceCents)}`
      : `${count} of ${total} US listings at ${formatDollarsFromCents(evaluation.priceCents)}`);
  } else {
    parts.push(`${formatDollarsFromCents(tier.low)}–${formatDollarsFromCents(tier.high)}` +
      (kind === 'verdict' ? ` (median ${formatDollarsFromCents(getDisplayTier(tier).mid)})` : ''));
    if (info.mixedConditions && info.conditionCounts) {
      const mix = Object.entries(info.conditionCounts)
        .filter(([slug]) => slug !== 'pxc')
        .map(([slug, n]) => `${n} ${SLUG_LABELS[slug] || slug}`)
        .join(', ');
      if (mix) parts.push(mix);
    }
  }

  if (info.coverage === 'partial') {
    if (Number.isFinite(info.apiTotal) && info.apiTotal > 0) {
      parts.push(`${info.totalListings} of ${info.apiTotal} US listings`);
    }
    parts.push('partial sample');
  }
  if (info.pxcCount > 0) parts.push(`includes ${info.pxcCount} PedalxChange`);
  if (info.mixedVersions) parts.push('mixed versions');
  for (const reason of info.reasons || []) {
    const phrase = REASON_PHRASES[reason];
    if (phrase && !parts.includes(phrase)) parts.push(phrase);
  }
  if (info.limited) parts.push('limited data');
  return parts.join(' · ');
}

// PXC condition label → the Reverb marketplace condition slug (hyphenated —
// these are URL filter values, distinct from the underscore doc keys above).
const CONDITION_TO_REVERB_SLUG = {
  New: 'new',
  Excellent: 'excellent',
  'Very Good': 'very-good',
  Good: 'good',
  Poor: 'fair',
  Damaged: 'poor',
};

/**
 * Destination for the "Reverb listings" attribution link: a marketplace
 * search for THIS pedal with the filters the benchmark actually used (US
 * sellers; the tier's condition; the vintage bucket's year window), never
 * the Reverb homepage. When the evidence mixes conditions (Excellent + Mint
 * supplementation), the condition filter is omitted so the destination
 * matches the broader basis — which the tooltip also disclosed.
 */
export function buildReverbSearchUrl({ make, model, condition, normDecade, mixedConditions }) {
  const params = new URLSearchParams();
  params.set('query', `${make || ''} ${model || ''}`.trim());
  params.set('product_type', 'effects-and-pedals');
  params.set('item_region', 'US');
  if (!mixedConditions) {
    const slug = CONDITION_TO_REVERB_SLUG[condition];
    if (slug) params.set('condition', slug);
  }
  const d = normalizeDecade(normDecade);
  if (d !== 'modern') {
    const start = Number(d.slice(0, 4));
    params.set('year_min', String(start));
    params.set('year_max', String(start + 9));
  }
  return `https://reverb.com/marketplace?${params.toString()}`;
}
